import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { foldForSearch } from '@shared';

import * as fromGenerated from '../../_generated';
import {
  AdminTeammateInvitee,
  AdminTeammatesAccessMode,
  AdminTeammatesActions,
  AdminTeammatesSortColumn,
  AdminTeammatesSortDirection,
} from './admin-teammates.actions';

export const adminTeammatesFeatureKey = 'adminTeammates';

export type AdminTeammatesModalKind = 'closed' | 'new' | 'edit';

export interface AdminTeammatesSort {
  column: AdminTeammatesSortColumn | null;
  direction: AdminTeammatesSortDirection;
}

export interface StagedTranslation {
  name: string;
  title: string;
  welcomeMessage: string;
  purposeMessage: string;
}

/** Builder-staged teammate. Avatar parts are kept as raw strings (the enum
 *  values) and cast to the generated enum types only at the DTO boundary. */
export interface StagedTeammate {
  color: string;
  shape: string;
  eyes: string;
  translations: { en: StagedTranslation; fr: StagedTranslation };
  attachedSkillIds: string[];
  accessMode: AdminTeammatesAccessMode;
  invitees: AdminTeammateInvitee[];
}

export interface AdminTeammatesModalState {
  kind: AdminTeammatesModalKind;
  teammate?: fromGenerated.AdminTeammateView;
  staged?: StagedTeammate;
  /** The invitee set at open time — the baseline for the save-time diff. */
  initialInvitees: AdminTeammateInvitee[];
  /** The attached-skill set at open time — the baseline for hasChanges. */
  initialSkillIds?: string[];
}

export interface AdminTeammatesState extends EntityState<fromGenerated.AdminTeammateView> {
  loaded: boolean;
  isLoading: boolean;
  isMutating: boolean;
  error: string | null;
  sort: AdminTeammatesSort;
  filter: string;
  modal: AdminTeammatesModalState;
  deleteConfirmTarget: fromGenerated.AdminTeammateView | undefined;
  dropdownPayloads: Record<string, fromGenerated.TeammateDropdownPayloadView>;
  // Builder data (loaded on open).
  allUsers: fromGenerated.AdminUserView[];
  allGroups: fromGenerated.GroupView[];
  allGroupId: string | null;
  skillSearchResults: fromGenerated.AdminSkillView[];
}

const entityAdapter: EntityAdapter<fromGenerated.AdminTeammateView> =
  createEntityAdapter<fromGenerated.AdminTeammateView>({
    selectId: (teammate) => teammate.id,
    sortComparer: false,
  });

const emptyTranslation = (): StagedTranslation => ({
  name: '',
  title: '',
  welcomeMessage: '',
  purposeMessage: '',
});

// Spec default avatar: Pentagon shape, color A, eye one.
const defaultStaged = (): StagedTeammate => ({
  color: 'A',
  shape: 'pentagon',
  eyes: 'one',
  translations: { en: emptyTranslation(), fr: emptyTranslation() },
  attachedSkillIds: [],
  accessMode: 'invited-only',
  invitees: [],
});

/**
 * Seed the staged form from a list-row view. The Builder displays both FR and EN
 * fields, so each locale slot is hydrated from its own stored translation in
 * `teammate.translations` (the flat `name`/`title`/… fields carry only the one
 * resolved display locale and are not enough). A locale absent from the array
 * stays empty — the genuine "no translation yet" state. Skills + invitees are
 * seeded asynchronously after their fetches.
 */
const stagedFromTeammate = (
  teammate: fromGenerated.AdminTeammateView,
): StagedTeammate => {
  const base = defaultStaged();
  base.color = teammate.color;
  base.shape = teammate.shape;
  base.eyes = teammate.eyes;

  const translations = teammate.translations ?? [];
  for (const t of translations) {
    const slot = t.locale === 'fr' ? 'fr' : 'en';
    base.translations[slot] = {
      name: t.name ?? '',
      title: t.title ?? '',
      welcomeMessage: t.welcomeMessage ?? '',
      purposeMessage: t.purposeMessage ?? '',
    };
  }
  // Defensive fallback for payloads that predate the `translations` array: seed
  // the resolved locale from the flat fields so editing never opens fully blank.
  if (translations.length === 0) {
    const slot = teammate.locale === 'fr' ? 'fr' : 'en';
    base.translations[slot] = {
      name: teammate.name ?? '',
      title: teammate.title ?? '',
      welcomeMessage: teammate.welcomeMessage ?? '',
      purposeMessage: teammate.purposeMessage ?? '',
    };
  }

  base.accessMode = teammate.hasAllGroupAccess ? 'org-wide' : 'invited-only';
  return base;
};

const inviteeKey = (invitee: { kind: string; id: string }): string =>
  `${invitee.kind}:${invitee.id}`;

export const initialAdminTeammatesState: AdminTeammatesState =
  entityAdapter.getInitialState({
    loaded: false,
    isLoading: false,
    isMutating: false,
    error: null,
    sort: { column: 'name', direction: 'asc' },
    filter: '',
    modal: { kind: 'closed', initialInvitees: [] },
    deleteConfirmTarget: undefined,
    dropdownPayloads: {},
    allUsers: [],
    allGroups: [],
    allGroupId: null,
    skillSearchResults: [],
  });

const matchesFilter = (
  teammate: fromGenerated.AdminTeammateView,
  filter: string,
): boolean => {
  const needle = foldForSearch(filter.trim());
  if (!needle) {
    return true;
  }
  return (
    foldForSearch(teammate.name ?? '').includes(needle) ||
    foldForSearch(teammate.title ?? '').includes(needle)
  );
};

const compareTeammatesByColumn = (
  a: fromGenerated.AdminTeammateView,
  b: fromGenerated.AdminTeammateView,
  column: AdminTeammatesSortColumn,
): number => {
  switch (column) {
    case 'name':
      return (a.name ?? '')
        .toLowerCase()
        .localeCompare((b.name ?? '').toLowerCase());
    case 'skills':
      return (a.skillCount ?? 0) - (b.skillCount ?? 0);
    case 'groups':
      return (a.groupCount ?? 0) - (b.groupCount ?? 0);
    case 'users':
      return (a.userCount ?? 0) - (b.userCount ?? 0);
  }
};

/** Apply an optimistic delta to a row and clear its cached dropdown payload. */
const adjustRow = (
  state: AdminTeammatesState,
  teammateId: string,
  changes: Partial<fromGenerated.AdminTeammateView>,
): AdminTeammatesState => {
  const cleared = { ...state.dropdownPayloads };
  delete cleared[teammateId];
  return entityAdapter.updateOne(
    { id: teammateId, changes },
    { ...state, dropdownPayloads: cleared },
  );
};

const withStaged = (
  state: AdminTeammatesState,
  update: (staged: StagedTeammate) => StagedTeammate,
): AdminTeammatesState => {
  if (!state.modal.staged) {
    return state;
  }
  return {
    ...state,
    modal: { ...state.modal, staged: update(state.modal.staged) },
  };
};

export const adminTeammatesFeature = createFeature({
  name: adminTeammatesFeatureKey,
  reducer: createReducer(
    initialAdminTeammatesState,

    on(AdminTeammatesActions.loadTeammates, (state) => ({
      ...state,
      isLoading: true,
      error: null,
    })),
    on(AdminTeammatesActions.loadTeammatesSuccess, (state, { teammates }) =>
      entityAdapter.setAll(teammates, {
        ...state,
        loaded: true,
        isLoading: false,
        error: null,
      }),
    ),
    on(AdminTeammatesActions.loadTeammatesFailure, (state, { error }) => ({
      ...state,
      error,
      isLoading: false,
    })),

    on(AdminTeammatesActions.setSort, (state, { column, direction }) => ({
      ...state,
      sort: { column, direction },
    })),
    on(AdminTeammatesActions.setFilter, (state, { filter }) => ({
      ...state,
      filter,
    })),

    on(
      AdminTeammatesActions.loadDropdownPayloadSuccess,
      (state, { teammateId, payload }) => ({
        ...state,
        dropdownPayloads: { ...state.dropdownPayloads, [teammateId]: payload },
      }),
    ),

    on(AdminTeammatesActions.loadAllUsersSuccess, (state, { users }) => ({
      ...state,
      allUsers: users,
    })),
    on(AdminTeammatesActions.loadAllGroupsSuccess, (state, { groups }) => ({
      ...state,
      allGroups: groups,
      // The org-wide group is the system 'all' group; cache its id for the
      // org-wide save path + the Decision O hasAllGroupAccess flip.
      allGroupId: groups.find((group) => group.type === 'all')?.id ?? null,
    })),

    // ─── Overlay lifecycle ───────────────────────────────────────────────────
    on(AdminTeammatesActions.openCreateModal, (state) => ({
      ...state,
      modal: { kind: 'new', staged: defaultStaged(), initialInvitees: [] },
      skillSearchResults: [],
    })),
    on(AdminTeammatesActions.openEditModal, (state, { teammate }) => ({
      ...state,
      modal: {
        kind: 'edit',
        teammate,
        staged: stagedFromTeammate(teammate),
        initialInvitees: [],
      },
      skillSearchResults: [],
    })),
    on(AdminTeammatesActions.seedEditInvitees, (state, { invitees }) =>
      withStaged(
        { ...state, modal: { ...state.modal, initialInvitees: invitees } },
        (staged) => ({
          ...staged,
          invitees,
        }),
      ),
    ),
    on(AdminTeammatesActions.seedEditSkills, (state, { skillIds }) => {
      if (!state.modal.staged) {
        return state;
      }
      return {
        ...state,
        modal: {
          ...state.modal,
          initialSkillIds: skillIds,
          staged: { ...state.modal.staged, attachedSkillIds: skillIds },
        },
      };
    }),
    on(AdminTeammatesActions.openDeleteConfirm, (state, { teammate }) => ({
      ...state,
      deleteConfirmTarget: teammate,
    })),
    on(AdminTeammatesActions.closeDeleteConfirm, (state) => ({
      ...state,
      deleteConfirmTarget: undefined,
    })),
    on(AdminTeammatesActions.closeModal, (state) => ({
      ...state,
      modal: { kind: 'closed', initialInvitees: [] },
    })),

    // ─── Staged-data mutations ───────────────────────────────────────────────
    on(AdminTeammatesActions.setAvatarPart, (state, { part, value }) =>
      withStaged(state, (staged) => ({ ...staged, [part]: value })),
    ),
    on(
      AdminTeammatesActions.setTranslationField,
      (state, { field, locale, value }) =>
        withStaged(state, (staged) => ({
          ...staged,
          translations: {
            ...staged.translations,
            [locale]: { ...staged.translations[locale], [field]: value },
          },
        })),
    ),
    // Decision N: the mode toggle never clears invitees — toggling back restores them.
    on(AdminTeammatesActions.setAccessMode, (state, { mode }) =>
      withStaged(state, (staged) => ({ ...staged, accessMode: mode })),
    ),
    on(AdminTeammatesActions.addInvitee, (state, { invitee }) =>
      withStaged(state, (staged) =>
        staged.invitees.some((i) => inviteeKey(i) === inviteeKey(invitee))
          ? staged
          : { ...staged, invitees: [...staged.invitees, invitee] },
      ),
    ),
    on(AdminTeammatesActions.removeInvitee, (state, { id, kind }) =>
      withStaged(state, (staged) => ({
        ...staged,
        invitees: staged.invitees.filter(
          (i) => !(i.id === id && i.kind === kind),
        ),
      })),
    ),
    on(AdminTeammatesActions.addAttachedSkill, (state, { skillId }) =>
      withStaged(state, (staged) =>
        staged.attachedSkillIds.includes(skillId)
          ? staged
          : {
              ...staged,
              attachedSkillIds: [...staged.attachedSkillIds, skillId],
            },
      ),
    ),
    on(AdminTeammatesActions.removeAttachedSkill, (state, { skillId }) =>
      withStaged(state, (staged) => ({
        ...staged,
        attachedSkillIds: staged.attachedSkillIds.filter(
          (id) => id !== skillId,
        ),
      })),
    ),

    // ─── Skills typeahead ────────────────────────────────────────────────────
    on(AdminTeammatesActions.searchSkillsSuccess, (state, { results }) => ({
      ...state,
      skillSearchResults: results,
    })),
    on(AdminTeammatesActions.searchSkillsFailure, (state) => ({
      ...state,
      skillSearchResults: [],
    })),

    // ─── Create / Update / Delete (isMutating + row mutations) ───────────────
    on(
      AdminTeammatesActions.createTeammate,
      AdminTeammatesActions.updateTeammateContent,
      AdminTeammatesActions.deleteTeammate,
      (state) => ({ ...state, isMutating: true }),
    ),
    on(AdminTeammatesActions.createTeammateSuccess, (state, { teammate }) =>
      entityAdapter.addOne(teammate, {
        ...state,
        isMutating: false,
        modal: { kind: 'closed', initialInvitees: [] },
      }),
    ),
    on(AdminTeammatesActions.createTeammateFailure, (state) => ({
      ...state,
      isMutating: false,
    })),
    on(
      AdminTeammatesActions.updateTeammateContentSuccess,
      (state, { teammate }) =>
        entityAdapter.updateOne(
          { id: teammate.id, changes: teammate },
          {
            ...state,
            isMutating: false,
            modal: { kind: 'closed', initialInvitees: [] },
          },
        ),
    ),
    on(AdminTeammatesActions.updateTeammateContentFailure, (state) => ({
      ...state,
      isMutating: false,
    })),
    on(AdminTeammatesActions.deleteTeammateSuccess, (state, { teammateId }) => {
      const cleared = { ...state.dropdownPayloads };
      delete cleared[teammateId];
      return entityAdapter.removeOne(teammateId, {
        ...state,
        isMutating: false,
        modal: { kind: 'closed', initialInvitees: [] },
        deleteConfirmTarget: undefined,
        dropdownPayloads: cleared,
      });
    }),
    on(AdminTeammatesActions.deleteTeammateFailure, (state) => ({
      ...state,
      isMutating: false,
    })),

    // ─── Decision O: optimistic row counts on each leaf success ──────────────
    on(
      AdminTeammatesActions.grantUserTeammateAccessSuccess,
      (state, { teammateId }) => {
        const current = state.entities[teammateId];
        if (!current) return state;
        return adjustRow(state, teammateId, {
          userCount: (current.userCount ?? 0) + 1,
        });
      },
    ),
    on(
      AdminTeammatesActions.revokeUserTeammateAccessSuccess,
      (state, { teammateId }) => {
        const current = state.entities[teammateId];
        if (!current) return state;
        return adjustRow(state, teammateId, {
          userCount: Math.max(0, (current.userCount ?? 0) - 1),
        });
      },
    ),
    on(
      AdminTeammatesActions.grantGroupTeammateAccessSuccess,
      (state, { teammateId, groupId }) => {
        const current = state.entities[teammateId];
        if (!current) return state;
        const isAll = state.allGroupId !== null && groupId === state.allGroupId;
        return adjustRow(state, teammateId, {
          groupCount: (current.groupCount ?? 0) + 1,
          ...(isAll ? { hasAllGroupAccess: true } : {}),
        });
      },
    ),
    on(
      AdminTeammatesActions.revokeGroupTeammateAccessSuccess,
      (state, { teammateId, groupId }) => {
        const current = state.entities[teammateId];
        if (!current) return state;
        const isAll = state.allGroupId !== null && groupId === state.allGroupId;
        return adjustRow(state, teammateId, {
          groupCount: Math.max(0, (current.groupCount ?? 0) - 1),
          ...(isAll ? { hasAllGroupAccess: false } : {}),
        });
      },
    ),
  ),
  extraSelectors: ({ selectAdminTeammatesState, selectSort, selectFilter }) => {
    const entitySelectors = entityAdapter.getSelectors(
      selectAdminTeammatesState,
    );

    const selectFilteredSortedTeammates = createSelector(
      entitySelectors.selectAll,
      selectSort,
      selectFilter,
      (teammates, sort, filter) => {
        const filtered = filter
          ? teammates.filter((teammate) => matchesFilter(teammate, filter))
          : teammates;
        if (!sort.column) {
          return filtered;
        }
        const sign = sort.direction === 'asc' ? 1 : -1;
        const column = sort.column;
        return [...filtered].sort(
          (a, b) => sign * compareTeammatesByColumn(a, b, column),
        );
      },
    );

    return {
      ...entitySelectors,
      selectFilteredSortedTeammates,
    };
  },
});
